/**
 * ConfiguredTutorial
 *
 * A generic, config-driven tutorial renderer that uses JSON configuration
 * instead of hardcoded step definitions. This allows new tutorials to be
 * created without code changes.
 */

import { FunctionComponent, useEffect, useMemo, useRef, useState } from "react"
import { useShepherdTour } from "react-shepherd"
import { useNavigate, useParams } from "react-router-dom"

import { useTypedDispatch, useTypedSelector } from "../../../app/store"
import { setMessages } from "../../simulation/simulationSlice"
import { convertToSeconds, resetSimulationState } from "../../simulation/utils"
import { setUserCompletedTutorial } from "../../auth/authSlice"
import { Information } from "../../simulation/types"

import { TutorialConfig } from "../types/tutorial-config"
import { tourOptions } from "../utils"
import { buildTreeNodeSelectorMap, resolveTreeNodeId } from "../utils/tree-resolver"
import { buildStepsFromConfig, StepBuilderContext } from "../utils/step-builder"

type ConfiguredTutorialProps = {
  config: TutorialConfig
  categories: Information[]
}

/**
 * Cleans up any orphaned Shepherd.js DOM elements.
 * This handles cases where React StrictMode creates duplicate tours.
 */
function cleanupShepherdElements(): void {
  // Remove all Shepherd step elements
  document.querySelectorAll(".shepherd-element").forEach((el) => el.remove())
  // Remove all modal overlay containers
  document.querySelectorAll(".shepherd-modal-overlay-container").forEach((el) => el.remove())
}

const ConfiguredTutorial: FunctionComponent<ConfiguredTutorialProps> = ({
  config,
  categories,
}) => {
  const dispatch = useTypedDispatch()
  const navigate = useNavigate()

  const { secondsElapsed } = useTypedSelector((state) => state.simulationSlice)
  const { activeChoiceConnections } = useTypedSelector(
    (state) => state.decisionsSlice
  )
  const { plans } = useTypedSelector((state) => state.simulationSlice)
  const durationSeconds = useTypedSelector((state) =>
    convertToSeconds(state.simulationSlice.duration)
  )
  const { testId: testIdString, runId } = useParams()

  // Build selector map from tree node mappings
  const selectorMap = useMemo(() => {
    return buildTreeNodeSelectorMap(categories, config.treeNodeMappings)
  }, [categories, config.treeNodeMappings])

  // Use a ref to store the tour complete function, avoiding dependency cycles
  const tourCompleteRef = useRef<() => void>(() => {})

  // Use a ref to store current secondsElapsed for message injection
  const secondsElapsedRef = useRef(secondsElapsed)
  useEffect(() => {
    secondsElapsedRef.current = secondsElapsed
  }, [secondsElapsed])

  // Build context for step builder - use stable references to avoid recreating steps
  const context: StepBuilderContext = useMemo(
    () => ({
      dispatch,
      navigate,
      testId: testIdString || "",
      runId: runId || "",
      selectorMap,
      categories,
      // Use a stable wrapper that delegates to the ref
      tourRef: { complete: () => tourCompleteRef.current() },
      // Getter for current seconds elapsed (for message timestamps)
      getSecondsElapsed: () => secondsElapsedRef.current,
    }),
    [dispatch, navigate, testIdString, runId, selectorMap, categories]
  )

  // Build Shepherd steps from config - only rebuilds when config or context changes
  const steps = useMemo(() => {
    return buildStepsFromConfig(config, context)
  }, [config, context])

  // Create the Shepherd tour
  const tour = useShepherdTour({ tourOptions, steps })
  const [tourRef] = useState(tour)

  // Update the ref after tour is created
  useEffect(() => {
    tourCompleteRef.current = () => tourRef.complete()
  }, [tourRef])

  // Initialize tutorial with proper cleanup for React StrictMode
  useEffect(() => {
    // Clean up any orphaned elements from previous mounts (StrictMode)
    cleanupShepherdElements()

    dispatch(setMessages([]))
    if (!tourRef.isActive()) {
      tourRef.start()
    }

    // Cleanup on unmount
    return () => {
      if (tourRef.isActive()) {
        tourRef.complete()
      }
      // Clean up DOM elements on unmount
      cleanupShepherdElements()
    }
  }, [config.simulationId, dispatch, tourRef])

  // Set up search triggers
  useEffect(() => {
    const handleTreeSearch = (e: globalThis.Event) => {
      const customEvent = e as CustomEvent
      const searchTerm = customEvent.detail?.debouncedSearchTerm?.toLowerCase()

      if (!searchTerm) return

      for (const trigger of config.searchTriggers) {
        const pattern = trigger.searchPattern.toLowerCase()
        if (searchTerm.includes(pattern)) {
          const treeContainer = document.getElementById("tree-container")
          treeContainer?.dispatchEvent(new CustomEvent(trigger.eventName))
        }
      }
    }

    document.addEventListener("treeSearchDebounced", handleTreeSearch)

    return () => {
      document.removeEventListener("treeSearchDebounced", handleTreeSearch)
    }
  }, [config.searchTriggers])

  // Set up tree node selection triggers
  useEffect(() => {
    const handleTreeNodeSelected = (e: globalThis.Event) => {
      const customEvent = e as CustomEvent
      const selectedNodeId = customEvent.detail?.selectedTreeItem?.id

      if (!selectedNodeId) return

      for (const trigger of config.treeNodeTriggers) {
        let targetNodeId: number | null = null

        if (trigger.nodeId !== undefined) {
          targetNodeId = trigger.nodeId
        } else if (trigger.nodePath) {
          targetNodeId = resolveTreeNodeId(categories, trigger.nodePath)
        }

        if (targetNodeId !== null && selectedNodeId === targetNodeId) {
          const treeContainer = document.getElementById("tree-container")
          treeContainer?.dispatchEvent(new CustomEvent(trigger.eventName))
        }
      }
    }

    document.addEventListener("treeNodeSelected", handleTreeNodeSelected)

    return () => {
      document.removeEventListener("treeNodeSelected", handleTreeNodeSelected)
    }
  }, [config.treeNodeTriggers, categories])

  // Redirect to simulation when practice time runs out
  useEffect(() => {
    if (!tourRef.isActive()) {
      if (durationSeconds === 0) return
      if (secondsElapsed + 3 >= durationSeconds) {
        dispatch(setUserCompletedTutorial(true))
        resetSimulationState(dispatch, false)
        navigate(`/simulations/${testIdString}/${runId}/intro`)
      }
    }
  }, [secondsElapsed, durationSeconds, tourRef, testIdString, runId])

  // Watch for user adding connections (configurable step ID)
  useEffect(() => {
    if (!config.watchConnectionsStepId) return

    const currentStep = tourRef.getCurrentStep()
    if (
      currentStep?.id === config.watchConnectionsStepId &&
      activeChoiceConnections.length > 0
    ) {
      tourRef.next()
    }
  }, [activeChoiceConnections, tourRef, config.watchConnectionsStepId])

  // Watch for user enacting a plan (configurable step ID)
  useEffect(() => {
    if (!config.watchPlanExecutionStepId) return

    const currentStep = tourRef.getCurrentStep()
    if (currentStep?.id === config.watchPlanExecutionStepId) {
      tourRef.next()
    }
  }, [plans, tourRef, config.watchPlanExecutionStepId])

  return <div style={{ display: "none" }}></div>
}

export default ConfiguredTutorial
